import { ShowArtifactToolOutput } from "@shaarp/common";

/** What a produced (non-refused) Artifact carries — the shape worth opening. */
export type ProducedArtifact = Extract<ShowArtifactToolOutput, { success: true }>;

/**
 * Narrows a `tool-showArtifact` output to a produced Artifact.
 *
 * Shared by the inline card (`chat-tool-show-artifact.tsx`) and the
 * auto-open effect (`chat-conversation.tsx`), which both have to tell a
 * successful call from a refusal (bad HTML, a failed upload) the same way —
 * checking every field of {@link ProducedArtifact} rather than just
 * `success`, so a malformed success shape cannot slip past as one.
 */
export function isArtifactProduced(
  output: unknown
): output is ProducedArtifact {
  return (
    !!output &&
    typeof output === "object" &&
    (output as ShowArtifactToolOutput).success === true &&
    typeof (output as { artifactId?: unknown }).artifactId === "string" &&
    typeof (output as { title?: unknown }).title === "string"
  );
}

/**
 * How much of the end of a document being written is read to show its latest
 * text: enough for a few lines, and a bounded parse on every stream delta
 * however long the document grows.
 */
const TEXT_TAIL_WINDOW_CHARS = 4000;

/**
 * The last `maxChars` of readable text in a partially written Artifact —
 * what the thread shows while the model is still writing it.
 *
 * Parsed with `DOMParser`, whose document is inert: no script runs and no
 * image, font or stylesheet is fetched. Only the text leaves it, rendered by
 * React as plain text: the streamed HTML has not been sanitized yet (that
 * happens server-side, on the finished document), so it is never rendered as
 * HTML here (`docs/rules/security.md`, Frontend).
 */
export function extractArtifactTextTail(
  html: string | undefined,
  maxChars: number
): string {
  if (!html) {
    return "";
  }
  let window = html.slice(-TEXT_TAIL_WINDOW_CHARS);
  // Cut into a long document, the window can open halfway through a tag,
  // whose end would otherwise read as text.
  if (window.length < html.length) {
    const tagEnd = window.indexOf(">");
    const tagStart = window.indexOf("<");
    if (tagEnd !== -1 && (tagStart === -1 || tagEnd < tagStart)) {
      window = window.slice(tagEnd + 1);
    }
  }
  const document = new DOMParser().parseFromString(window, "text/html");
  document
    .querySelectorAll("style, script, noscript, template, title")
    .forEach((element) => element.remove());

  const texts: string[] = [];
  const walker = document.createTreeWalker(
    document.documentElement,
    NodeFilter.SHOW_TEXT
  );
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.textContent?.trim();
    if (text) {
      texts.push(text);
    }
  }
  const text = texts.join(" ").replace(/\s+/g, " ");
  return text.length > maxChars ? text.slice(-maxChars).trimStart() : text;
}

/** The two tool parts that produce a version of an Artifact. */
const ARTIFACT_TOOL_PART_TYPES = new Set([
  "tool-showArtifact",
  "tool-editArtifact",
]);

/**
 * Every settled, successful Artifact call among a message's `parts` — a new
 * document or an edit of one — with the call that produced it. Keyed by call
 * rather than by document: an edit produces a new version of the same
 * `artifactId`, and each version has to reach the panel.
 */
export function findProducedArtifacts(
  parts: readonly unknown[]
): { toolCallId: string; artifact: ProducedArtifact }[] {
  return parts.flatMap((part) => {
    const { type, state, toolCallId, output } = part as {
      type?: unknown;
      state?: unknown;
      toolCallId?: unknown;
      output?: unknown;
    };
    if (
      typeof type !== "string" ||
      !ARTIFACT_TOOL_PART_TYPES.has(type) ||
      state !== "output-available" ||
      typeof toolCallId !== "string" ||
      !isArtifactProduced(output)
    ) {
      return [];
    }
    return [{ toolCallId, artifact: output }];
  });
}
